import { useState, useRef } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import {
  Lightbulb,
  CaretDown,
  CaretUp,
  Printer,
  User,
  SunDim,
  CircleHalf,
  Gradient,
  Star,
  Spotlight,
  Package,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { usePDFExport } from "@/utils/pdfExport";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const content = {
  en: {
    page_title: "Lighting Techniques",
    page_sub: "Master the art of light — from portrait setups to professional studio kits.",
    download: "Download as PDF",
    setup: "Setup",
    when: "When to Use",
    tips: "Pro Tips",
    section_portrait: "Portrait Lighting Patterns",
    section_portrait_sub: "The foundation of studio and natural-light portrait photography.",
    section_kits: "Photography Light Kits",
    section_kits_sub: "Common lighting equipment and kits used by photographers at every level.",
    includes: "Typically Includes",
    best_for: "Best For",
    budget: "Budget Range",
    techniques: [
      {
        id: "loop",
        title: "Loop Lighting",
        desc: "Creates a small shadow on one side of the nose that loops down toward the corner of the mouth. The most commonly used portrait lighting pattern.",
        setup: "Position the key light about 30-45 degrees to the side and slightly above eye level. The shadow from the nose should not touch the shadow on the cheek.",
        when: "General portraits, headshots, and everyday studio work. Flattering for most face shapes.",
        tips: ["Keep the nose shadow small — it should not touch the lip shadow.", "Works well with a fill light or reflector on the opposite side.", "The classic starting point — master this first."],
      },
      {
        id: "butterfly",
        title: "Butterfly (Paramount) Lighting",
        desc: "Creates a symmetrical butterfly-shaped shadow directly under the nose. Named 'Paramount' because Hollywood studios used it extensively for glamour shots.",
        setup: "Place the key light directly in front of the subject and above eye level, aimed slightly downward. The photographer typically shoots from just below the light.",
        when: "Glamour photography, beauty shots, and subjects with prominent cheekbones. Very flattering for women.",
        tips: ["Add a reflector below the chin to fill shadows under the eyes.", "Higher light position = more dramatic shadow.", "Also called 'clamshell' when combined with a fill below."],
      },
      {
        id: "split",
        title: "Split Lighting",
        desc: "Illuminates exactly half the face, leaving the other half in shadow. Creates the most dramatic and moody portrait look.",
        setup: "Place the key light at a 90-degree angle to the subject, directly to the side. No fill light for maximum drama.",
        when: "Dramatic portraits, artistic/moody headshots, and character studies. Often used for musicians and athletes.",
        tips: ["Works best with one light source and no fill.", "The shadow line should run straight down the center of the face.", "Not recommended for corporate headshots — too dramatic."],
      },
      {
        id: "rembrandt",
        title: "Rembrandt Lighting",
        desc: "Creates a triangle of light on the shadowed cheek. Named after the painter Rembrandt who frequently used this light pattern in his paintings.",
        setup: "Position the key light about 45 degrees to the side and above eye level. Adjust until a small triangle of light appears on the shadow-side cheek. The triangle should not be larger than the eye and nose.",
        when: "Artistic portraits, character portraits, and editorial work. Adds depth and dimension.",
        tips: ["The triangle should be no wider than the eye and no longer than the nose.", "Works beautifully with a single light and dark background.", "Slightly turn the subject's face away from the light for the best triangle."],
      },
      {
        id: "broad",
        title: "Broad Lighting",
        desc: "The side of the face closest to the camera is lit. Makes the face appear wider — useful for narrow or thin faces.",
        setup: "Turn the subject's face slightly away from the camera. Place the key light on the same side as the face that's visible to the camera (the broad side).",
        when: "Subjects with narrow or thin faces. Adds visual width to the face.",
        tips: ["Avoid for round faces — it will make them look wider.", "Combine with short lighting for variety in the same session.", "The lit side faces the camera."],
      },
      {
        id: "short",
        title: "Short Lighting",
        desc: "The side of the face away from the camera is lit. Creates more shadow on the visible side, making the face appear thinner and more sculpted.",
        setup: "Turn the subject's face slightly toward the camera. Place the key light on the far side of the face (the side turned away from the camera).",
        when: "Most portrait situations. Slimming effect. Great for round or wide faces.",
        tips: ["More shadow on the camera-facing side creates depth.", "The most universally flattering lighting angle.", "The shadowed side faces the camera."],
      },
      {
        id: "rim",
        title: "Rim / Edge Lighting",
        desc: "Light placed behind the subject creates a bright outline or 'rim' around the subject's edges, separating them from the background.",
        setup: "Place a light behind and to the side of the subject, pointed toward the camera. The light should skim the edge of the subject's hair, shoulders, or body without hitting the lens directly.",
        when: "Separating the subject from dark backgrounds. Adding drama and depth. Hair lights in studio portraiture.",
        tips: ["Use a flag or barn doors to prevent lens flare.", "Often used as a secondary light in combination with a key light.", "Also called 'hair light' or 'kicker' depending on placement."],
      },
      {
        id: "clamshell",
        title: "Clamshell Lighting",
        desc: "Two lights positioned above and below the subject create even, shadow-free illumination. Named for the clamshell shape of the light setup when viewed from the side.",
        setup: "Place the main light above and slightly in front of the subject. Place a fill light (or reflector) below the chin, angled upward. Both lights should be soft (use softboxes or umbrellas).",
        when: "Beauty photography, fashion, headshots, and any situation where you want minimal shadows.",
        tips: ["The bottom light should be 1-2 stops weaker than the top light.", "Use a white reflector below instead of a second light for a simpler setup.", "Creates the 'catchlights' at 12 and 6 o'clock in the eyes."],
      },
      {
        id: "highkey",
        title: "High Key Lighting",
        desc: "Bright, even lighting with minimal shadows. The entire image is intentionally overexposed for a clean, airy, optimistic feel.",
        setup: "Use multiple large, soft light sources. Light the background separately to pure white. Fill all shadows. Typical ratio: background 1-2 stops brighter than the subject.",
        when: "Beauty, fashion, product photography, medical/dental portraits, and children's photography.",
        tips: ["Overexpose the background but not the subject.", "Use at least 3 lights: key, fill, and background.", "Avoid any hard shadows — everything should be soft and bright."],
      },
      {
        id: "lowkey",
        title: "Low Key Lighting",
        desc: "Predominantly dark image with selective, dramatic lighting. The majority of the frame is in shadow with only key areas illuminated.",
        setup: "Use a single hard light source with minimal fill. Dark or black background. Control light spill with flags, grids, or snoots. Keep light only on the areas you want to highlight.",
        when: "Dramatic portraits, fine art, moody editorial, film noir style, and artistic character studies.",
        tips: ["Less is more — one carefully placed light is all you need.", "Use black cards (flags) to subtract light from areas you want dark.", "A dark environment is essential — control every light source in the room."],
      },
    ],
    kits: [
      {
        id: "speedlight",
        title: "Speedlight / Flash Kit",
        desc: "Portable on-camera or off-camera flash units. The most versatile and affordable lighting option for photographers on the go.",
        includes: "1-2 speedlights, wireless triggers, flash stands, umbrellas or small softboxes, gel filters.",
        best_for: "Event photography, weddings, portraits on location, run-and-gun shooting.",
        budget: "$100 – $600",
      },
      {
        id: "strobe",
        title: "Studio Strobe / Monolight Kit",
        desc: "Powerful studio flash units with modeling lights. The backbone of professional studio photography.",
        includes: "2-3 monolights (200-600W), light stands, softboxes, reflectors, wireless triggers, carrying case.",
        best_for: "Studio portraiture, product photography, fashion, commercial work.",
        budget: "$300 – $2,000+",
      },
      {
        id: "continuous_led",
        title: "Continuous LED Panel Kit",
        desc: "Always-on LED lights that let you see exactly what you're getting. Perfect for both photo and video.",
        includes: "2-3 LED panels (bi-color or RGB), light stands, diffusion panels, AC adapters and batteries.",
        best_for: "Video production, YouTube/vlogging, product photography, interviews, beginners learning lighting.",
        budget: "$80 – $1,000",
      },
      {
        id: "softbox",
        title: "Softbox Kit",
        desc: "Large fabric light modifiers that create soft, directional, wrap-around light. Available for both strobes and continuous lights.",
        includes: "2-3 softboxes (rectangular, octagonal, or strip), light stands, speed rings, bulbs or strobes.",
        best_for: "Portrait photography, headshots, product photography, beauty shots.",
        budget: "$60 – $500",
      },
      {
        id: "umbrella",
        title: "Umbrella Kit",
        desc: "The simplest and most affordable light modifiers. Shoot-through (translucent) or reflective (silver/white lined).",
        includes: "2-3 umbrellas (shoot-through and/or reflective), light stands, socket adapters, bulbs.",
        best_for: "Beginners, event photography, quick portrait setups, group photos.",
        budget: "$30 – $200",
      },
      {
        id: "ringlight",
        title: "Ring Light Kit",
        desc: "Circular light that wraps evenly around the camera lens, creating signature circular catchlights in the eyes and shadow-free illumination.",
        includes: "LED ring light (12-18 inch), adjustable stand, phone/camera mount, color temperature controls.",
        best_for: "Beauty photography, makeup tutorials, social media content, macro photography.",
        budget: "$25 – $300",
      },
      {
        id: "beauty_dish",
        title: "Beauty Dish Kit",
        desc: "A parabolic reflector that creates light softer than a bare strobe but harder than a softbox. Produces a focused, wrapping light with defined edges.",
        includes: "Beauty dish (16-22 inch), grid attachment, diffusion sock, light stand, strobe adapter.",
        best_for: "Fashion photography, beauty/glamour portraits, editorial work.",
        budget: "$50 – $400",
      },
      {
        id: "reflector",
        title: "Reflector / Diffuser Kit",
        desc: "Passive light modifiers that bounce or diffuse existing light. No electricity needed — the most portable lighting tool.",
        includes: "5-in-1 reflector (silver, gold, white, black, translucent), reflector holder/arm, light stand.",
        best_for: "Outdoor portraits, fill light on location, product photography, controlling natural light.",
        budget: "$15 – $100",
      },
    ],
  },
  es: {
    page_title: "Tecnicas de Iluminacion",
    page_sub: "Domina el arte de la luz — desde montajes de retrato hasta kits de estudio profesionales.",
    download: "Descargar como PDF",
    setup: "Montaje",
    when: "Cuando Usarlo",
    tips: "Consejos Pro",
    section_portrait: "Patrones de Iluminacion para Retratos",
    section_portrait_sub: "La base de la fotografia de retrato en estudio y con luz natural.",
    section_kits: "Kits de Iluminacion Fotografica",
    section_kits_sub: "Equipo de iluminacion comun usado por fotografos de todos los niveles.",
    includes: "Incluye Tipicamente",
    best_for: "Ideal Para",
    budget: "Rango de Precio",
    techniques: [
      {
        id: "loop",
        title: "Iluminacion Loop",
        desc: "Crea una pequena sombra en un lado de la nariz que desciende hacia la comisura de la boca. El patron de iluminacion para retratos mas utilizado.",
        setup: "Posiciona la luz principal a unos 30-45 grados al lado y ligeramente por encima del nivel de los ojos. La sombra de la nariz no debe tocar la sombra de la mejilla.",
        when: "Retratos generales, fotos de perfil y trabajo de estudio cotidiano. Favorecedor para la mayoria de formas de rostro.",
        tips: ["Manten la sombra de la nariz pequena — no debe tocar la sombra del labio.", "Funciona bien con luz de relleno o reflector en el lado opuesto.", "El punto de partida clasico — domina esto primero."],
      },
      {
        id: "butterfly",
        title: "Iluminacion Mariposa (Paramount)",
        desc: "Crea una sombra simetrica en forma de mariposa directamente debajo de la nariz. Llamada 'Paramount' porque los estudios de Hollywood la usaban extensamente para fotos de glamour.",
        setup: "Coloca la luz principal directamente frente al sujeto y por encima del nivel de los ojos, apuntando ligeramente hacia abajo. El fotografo tipicamente dispara justo debajo de la luz.",
        when: "Fotografia de glamour, fotos de belleza y sujetos con pomulos prominentes. Muy favorecedor para mujeres.",
        tips: ["Anade un reflector debajo de la barbilla para rellenar sombras bajo los ojos.", "Posicion de luz mas alta = sombra mas dramatica.", "Tambien llamada 'clamshell' cuando se combina con relleno abajo."],
      },
      {
        id: "split",
        title: "Iluminacion Dividida (Split)",
        desc: "Ilumina exactamente la mitad del rostro, dejando la otra mitad en sombra. Crea el look de retrato mas dramatico y melancolico.",
        setup: "Coloca la luz principal a 90 grados del sujeto, directamente al lado. Sin luz de relleno para maximo drama.",
        when: "Retratos dramaticos, fotos artisticas/melancolicas y estudios de personaje. Usado frecuentemente para musicos y atletas.",
        tips: ["Funciona mejor con una sola fuente de luz y sin relleno.", "La linea de sombra debe correr directamente por el centro del rostro.", "No recomendado para fotos corporativas — demasiado dramatico."],
      },
      {
        id: "rembrandt",
        title: "Iluminacion Rembrandt",
        desc: "Crea un triangulo de luz en la mejilla sombreada. Nombrada por el pintor Rembrandt quien frecuentemente usaba este patron de luz en sus pinturas.",
        setup: "Posiciona la luz principal a unos 45 grados al lado y por encima del nivel de los ojos. Ajusta hasta que aparezca un pequeno triangulo de luz en la mejilla del lado en sombra.",
        when: "Retratos artisticos, retratos de personaje y trabajo editorial. Anade profundidad y dimension.",
        tips: ["El triangulo no debe ser mas ancho que el ojo ni mas largo que la nariz.", "Funciona hermosamente con una sola luz y fondo oscuro.", "Gira ligeramente el rostro del sujeto alejandolo de la luz para el mejor triangulo."],
      },
      {
        id: "broad",
        title: "Iluminacion Amplia (Broad)",
        desc: "El lado del rostro mas cercano a la camara esta iluminado. Hace que el rostro parezca mas ancho — util para rostros estrechos o delgados.",
        setup: "Gira el rostro del sujeto ligeramente alejandolo de la camara. Coloca la luz principal en el mismo lado del rostro visible a la camara (el lado amplio).",
        when: "Sujetos con rostros estrechos o delgados. Anade anchura visual al rostro.",
        tips: ["Evitar para rostros redondos — los hara parecer mas anchos.", "Combina con iluminacion corta para variedad en la misma sesion.", "El lado iluminado mira hacia la camara."],
      },
      {
        id: "short",
        title: "Iluminacion Corta (Short)",
        desc: "El lado del rostro alejado de la camara esta iluminado. Crea mas sombra en el lado visible, haciendo que el rostro parezca mas delgado y esculpido.",
        setup: "Gira el rostro del sujeto ligeramente hacia la camara. Coloca la luz principal en el lado lejano del rostro (el lado girado de la camara).",
        when: "La mayoria de situaciones de retrato. Efecto adelgazante. Excelente para rostros redondos o anchos.",
        tips: ["Mas sombra en el lado que mira a la camara crea profundidad.", "El angulo de iluminacion mas universalmente favorecedor.", "El lado sombreado mira hacia la camara."],
      },
      {
        id: "rim",
        title: "Iluminacion de Borde (Rim)",
        desc: "Luz colocada detras del sujeto crea un contorno brillante alrededor de los bordes del sujeto, separandolo del fondo.",
        setup: "Coloca una luz detras y al lado del sujeto, apuntando hacia la camara. La luz debe rozar el borde del cabello, hombros o cuerpo del sujeto sin golpear el lente directamente.",
        when: "Separar al sujeto de fondos oscuros. Anadir drama y profundidad. Luces de cabello en retrato de estudio.",
        tips: ["Usa banderas o viseras para prevenir destello en el lente.", "Frecuentemente usada como luz secundaria en combinacion con luz principal.", "Tambien llamada 'luz de cabello' o 'kicker' dependiendo de la posicion."],
      },
      {
        id: "clamshell",
        title: "Iluminacion Clamshell",
        desc: "Dos luces posicionadas arriba y abajo del sujeto crean iluminacion uniforme y sin sombras. Nombrada por la forma de almeja del montaje visto desde el lado.",
        setup: "Coloca la luz principal arriba y ligeramente frente al sujeto. Coloca una luz de relleno (o reflector) debajo de la barbilla, angulada hacia arriba. Ambas luces deben ser suaves.",
        when: "Fotografia de belleza, moda, fotos de perfil y cualquier situacion donde quieras sombras minimas.",
        tips: ["La luz inferior debe ser 1-2 pasos mas debil que la superior.", "Usa un reflector blanco abajo en vez de segunda luz para montaje mas simple.", "Crea los 'catchlights' a las 12 y 6 en punto en los ojos."],
      },
      {
        id: "highkey",
        title: "Iluminacion High Key (Clave Alta)",
        desc: "Iluminacion brillante y uniforme con sombras minimas. La imagen completa esta intencionalmente sobreexpuesta para un aspecto limpio, aereo y optimista.",
        setup: "Usa multiples fuentes de luz grandes y suaves. Ilumina el fondo por separado a blanco puro. Rellena todas las sombras. Proporcion tipica: fondo 1-2 pasos mas brillante que el sujeto.",
        when: "Belleza, moda, fotografia de producto, retratos medicos/dentales y fotografia infantil.",
        tips: ["Sobreexponer el fondo pero no el sujeto.", "Usa al menos 3 luces: principal, relleno y fondo.", "Evita cualquier sombra dura — todo debe ser suave y brillante."],
      },
      {
        id: "lowkey",
        title: "Iluminacion Low Key (Clave Baja)",
        desc: "Imagen predominantemente oscura con iluminacion selectiva y dramatica. La mayoria del encuadre esta en sombra con solo areas clave iluminadas.",
        setup: "Usa una sola fuente de luz dura con relleno minimo. Fondo oscuro o negro. Controla el derrame de luz con banderas, rejillas o snoots.",
        when: "Retratos dramaticos, arte, editorial melancolico, estilo film noir y estudios de personaje artisticos.",
        tips: ["Menos es mas — una luz cuidadosamente colocada es todo lo que necesitas.", "Usa cartulinas negras (banderas) para restar luz de areas que quieres oscuras.", "Un ambiente oscuro es esencial — controla cada fuente de luz en la habitacion."],
      },
    ],
    kits: [
      {
        id: "speedlight",
        title: "Kit de Speedlight / Flash",
        desc: "Unidades de flash portatiles para camara o fuera de camara. La opcion de iluminacion mas versatil y accesible para fotografos en movimiento.",
        includes: "1-2 speedlights, disparadores inalambricos, soportes de flash, sombrillas o softboxes pequenos, filtros de gel.",
        best_for: "Fotografia de eventos, bodas, retratos en locacion, disparo rapido.",
        budget: "$100 – $600",
      },
      {
        id: "strobe",
        title: "Kit de Strobe de Estudio / Monolight",
        desc: "Unidades de flash de estudio potentes con luces de modelado. La columna vertebral de la fotografia de estudio profesional.",
        includes: "2-3 monolights (200-600W), soportes de luz, softboxes, reflectores, disparadores inalambricos, estuche.",
        best_for: "Retrato de estudio, fotografia de producto, moda, trabajo comercial.",
        budget: "$300 – $2,000+",
      },
      {
        id: "continuous_led",
        title: "Kit de Panel LED Continuo",
        desc: "Luces LED siempre encendidas que te permiten ver exactamente lo que obtendras. Perfectas para foto y video.",
        includes: "2-3 paneles LED (bi-color o RGB), soportes de luz, paneles de difusion, adaptadores AC y baterias.",
        best_for: "Produccion de video, YouTube/vlogging, fotografia de producto, entrevistas, principiantes aprendiendo iluminacion.",
        budget: "$80 – $1,000",
      },
      {
        id: "softbox",
        title: "Kit de Softbox",
        desc: "Grandes modificadores de luz de tela que crean luz suave, direccional y envolvente. Disponibles para strobes y luces continuas.",
        includes: "2-3 softboxes (rectangulares, octagonales o strip), soportes de luz, anillos de velocidad, bombillas o strobes.",
        best_for: "Fotografia de retrato, fotos de perfil, fotografia de producto, tomas de belleza.",
        budget: "$60 – $500",
      },
      {
        id: "umbrella",
        title: "Kit de Sombrilla",
        desc: "Los modificadores de luz mas simples y accesibles. Translucidas (shoot-through) o reflectivas (con forro plateado/blanco).",
        includes: "2-3 sombrillas (translucidas y/o reflectivas), soportes de luz, adaptadores de socket, bombillas.",
        best_for: "Principiantes, fotografia de eventos, montajes rapidos de retrato, fotos grupales.",
        budget: "$30 – $200",
      },
      {
        id: "ringlight",
        title: "Kit de Aro de Luz",
        desc: "Luz circular que envuelve uniformemente alrededor del lente de la camara, creando catchlights circulares caracteristicos en los ojos.",
        includes: "Aro de luz LED (12-18 pulgadas), soporte ajustable, montura de telefono/camara, controles de temperatura de color.",
        best_for: "Fotografia de belleza, tutoriales de maquillaje, contenido para redes sociales, fotografia macro.",
        budget: "$25 – $300",
      },
      {
        id: "beauty_dish",
        title: "Kit de Beauty Dish",
        desc: "Un reflector parabolico que crea luz mas suave que un strobe directo pero mas dura que un softbox. Produce luz enfocada y envolvente con bordes definidos.",
        includes: "Beauty dish (16-22 pulgadas), rejilla, calcetin de difusion, soporte de luz, adaptador de strobe.",
        best_for: "Fotografia de moda, retratos de belleza/glamour, trabajo editorial.",
        budget: "$50 – $400",
      },
      {
        id: "reflector",
        title: "Kit de Reflector / Difusor",
        desc: "Modificadores de luz pasivos que rebotan o difunden la luz existente. No necesitan electricidad — la herramienta de iluminacion mas portatil.",
        includes: "Reflector 5-en-1 (plateado, dorado, blanco, negro, translucido), soporte/brazo de reflector, soporte de luz.",
        best_for: "Retratos al aire libre, luz de relleno en locacion, fotografia de producto, controlar luz natural.",
        budget: "$15 – $100",
      },
    ],
  },
};

export default function LightingPage() {
  const { lang } = useTranslation();
  const c = content[lang] || content.en;
  const [expanded, setExpanded] = useState(null);
  const [expandedKit, setExpandedKit] = useState(null);
  const printRef = useRef(null);

  const { ready, generating, download } = usePDFExport(
    printRef,
    () => `lighting-techniques-${lang}.pdf`,
    [lang]
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black" data-testid="lighting-page">
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
          <motion.div variants={item} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">{c.page_title}</h1>
              <p className="text-[#71717A] text-base">{c.page_sub}</p>
            </div>
            <Button onClick={download} disabled={!ready} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60" data-testid="lighting-download-btn">
              <Printer className="w-4 h-4 mr-1.5" />
              {generating ? (lang === "es" ? "Generando..." : "Generating...") : c.download}
            </Button>
          </motion.div>

          {/* Portrait Lighting Section */}
          <motion.div variants={item}>
            <div className="flex items-center gap-2 mb-1">
              <div className="h-px flex-1 bg-gradient-to-r from-[#F5A524]/40 to-transparent" />
              <span className="text-[#F5A524] text-xs font-medium uppercase tracking-[0.2em]">{c.section_portrait}</span>
              <div className="h-px flex-1 bg-gradient-to-l from-[#F5A524]/40 to-transparent" />
            </div>
            <p className="text-[#71717A] text-sm text-center mb-4">{c.section_portrait_sub}</p>
          </motion.div>

          {c.techniques.map((tech) => {
            const isOpen = expanded === tech.id;
            return (
              <motion.div key={tech.id} variants={item} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden" data-testid={`tech-${tech.id}`}>
                <button onClick={() => setExpanded(isOpen ? null : tech.id)} className="w-full flex items-center gap-4 p-5 text-left" data-testid={`tech-toggle-${tech.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#F5A524]/10 flex items-center justify-center shrink-0">
                    <Lightbulb weight="fill" className="w-5 h-5 text-[#F5A524]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Outfit'] text-white font-medium">{tech.title}</h3>
                    <p className="text-[#71717A] text-sm truncate">{tech.desc}</p>
                  </div>
                  {isOpen ? <CaretUp className="w-5 h-5 text-[#71717A] shrink-0" /> : <CaretDown className="w-5 h-5 text-[#71717A] shrink-0" />}
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                      <div className="px-5 pb-5 space-y-3 border-t border-white/5 pt-4">
                        <p className="text-[#D4D4D8] text-sm leading-relaxed">{tech.desc}</p>
                        <div><h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">{c.setup}</h4><p className="text-[#A1A1AA] text-sm">{tech.setup}</p></div>
                        <div><h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">{c.when}</h4><p className="text-[#A1A1AA] text-sm">{tech.when}</p></div>
                        <div><h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">{c.tips}</h4>
                          <div className="space-y-1">{tech.tips.map((tip, i) => (<div key={i} className="flex items-start gap-2"><Star weight="fill" className="w-3 h-3 text-[#F5A524] mt-1 shrink-0" /><p className="text-[#A1A1AA] text-sm">{tip}</p></div>))}</div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}

          {/* Light Kits Section */}
          <motion.div variants={item} className="mt-8">
            <div className="flex items-center gap-2 mb-1">
              <div className="h-px flex-1 bg-gradient-to-r from-[#F5A524]/40 to-transparent" />
              <span className="text-[#F5A524] text-xs font-medium uppercase tracking-[0.2em]">{c.section_kits}</span>
              <div className="h-px flex-1 bg-gradient-to-l from-[#F5A524]/40 to-transparent" />
            </div>
            <p className="text-[#71717A] text-sm text-center mb-4">{c.section_kits_sub}</p>
          </motion.div>

          {c.kits.map((kit) => {
            const isOpen = expandedKit === kit.id;
            return (
              <motion.div key={kit.id} variants={item} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden" data-testid={`kit-${kit.id}`}>
                <button onClick={() => setExpandedKit(isOpen ? null : kit.id)} className="w-full flex items-center gap-4 p-5 text-left" data-testid={`kit-toggle-${kit.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#F5A524]/10 flex items-center justify-center shrink-0">
                    <Package weight="fill" className="w-5 h-5 text-[#F5A524]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Outfit'] text-white font-medium">{kit.title}</h3>
                    <p className="text-[#71717A] text-sm truncate">{kit.desc}</p>
                  </div>
                  {isOpen ? <CaretUp className="w-5 h-5 text-[#71717A] shrink-0" /> : <CaretDown className="w-5 h-5 text-[#71717A] shrink-0" />}
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                      <div className="px-5 pb-5 space-y-3 border-t border-white/5 pt-4">
                        <p className="text-[#D4D4D8] text-sm leading-relaxed">{kit.desc}</p>
                        <div><h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">{c.includes}</h4><p className="text-[#A1A1AA] text-sm">{kit.includes}</p></div>
                        <div><h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">{c.best_for}</h4><p className="text-[#A1A1AA] text-sm">{kit.best_for}</p></div>
                        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
                          <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">{c.budget}</h4>
                          <p className="text-white text-sm font-['JetBrains_Mono']">{kit.budget}</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* Print-only version */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4">
        <h1 className="text-3xl font-bold text-center mb-2">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-6">{c.page_sub}</p>
        <h2 className="text-xl font-bold mb-3 border-b-2 border-gray-200 pb-2">{c.section_portrait}</h2>
        {c.techniques.map((tech, i) => (
          <div key={i} className="mb-4 break-inside-avoid">
            <h3 className="font-bold text-lg">{tech.title}</h3>
            <p className="text-sm text-gray-700 mb-1">{tech.desc}</p>
            <p className="text-sm"><strong>{c.setup}:</strong> {tech.setup}</p>
            <p className="text-sm"><strong>{c.when}:</strong> {tech.when}</p>
            <p className="text-sm"><strong>{c.tips}:</strong> {tech.tips.join(" | ")}</p>
          </div>
        ))}
        <h2 className="text-xl font-bold mb-3 border-b-2 border-gray-200 pb-2 mt-6">{c.section_kits}</h2>
        {c.kits.map((kit, i) => (
          <div key={i} className="mb-4 break-inside-avoid">
            <h3 className="font-bold text-lg">{kit.title}</h3>
            <p className="text-sm text-gray-700 mb-1">{kit.desc}</p>
            <p className="text-sm"><strong>{c.includes}:</strong> {kit.includes}</p>
            <p className="text-sm"><strong>{c.best_for}:</strong> {kit.best_for}</p>
            <p className="text-sm"><strong>{c.budget}:</strong> {kit.budget}</p>
          </div>
        ))}
        <p className="text-center text-xs text-gray-400 mt-6">www.dslradvisor.com</p>
      </div>
    </div>
  );
}
